import type { OnboardingChecklist, TaskKey } from '../hooks/useOnboarding';

/**
 * Riconciliazione checklist onboarding con lo stato reale dell'organizzazione.
 *
 * La checklist è event-driven client-side: i task si spuntano solo via
 * `onboarding:task-completed`. Chi ha configurato il CRM prima che esistessero
 * i dispatch (o da percorsi che non li emettono: import, API, mobile) resta
 * con i task fermi a `false` per sempre. Questo modulo controlla i dati reali
 * e spunta i task già di fatto completati.
 *
 * Criteri (attenzione al seeding di registrazione — vedi setup-organization):
 * - `profile`: la registrazione seeda business_name/phone/email in `settings`,
 *   quindi contano solo i campi che l'utente inserisce a mano (P.IVA o sede).
 * - `pipeline`: la registrazione seeda 1 pipeline con 5 stage per TUTTE le org,
 *   quindi "esiste una pipeline" non basta: la consideriamo impostata quando
 *   è anche in uso (almeno un lead/cliente in anagrafica).
 * - Gli altri task non hanno seeding: basta l'esistenza di almeno una riga.
 * - `referral` resta solo event-driven (nessun segnale affidabile nel DB).
 */

export interface OrgOnboardingSnapshot {
  companyProfileFilled: boolean;
  pipelineCount: number;
  stageCount: number;
  clientCount: number;
  dealCount: number;
  salesTargetCount: number;
  memberCount: number;
  workflowCount: number;
  integrationCount: number;
  wahaConfigCount: number;
}

/**
 * Il profilo azienda conta come compilato solo con P.IVA o sede presenti:
 * sono i campi promessi dal task ("P.IVA, sede, contatti") e gli unici NON
 * seedati dalla registrazione (business_name/phone/email arrivano già pieni).
 */
export function isCompanyProfileFilled(
  settings: { vat_number?: string | null; street_address?: string | null } | null,
): boolean {
  if (!settings) return false;
  return Boolean(settings.vat_number?.trim() || settings.street_address?.trim());
}

/**
 * Calcola i task da spuntare confrontando lo snapshot reale con la checklist.
 * Pura: nessun side effect, testabile in isolamento.
 */
export function computeReconciledTasks(
  snapshot: OrgOnboardingSnapshot,
  checklist: OnboardingChecklist | null,
): TaskKey[] {
  const done = (t: TaskKey): boolean => checklist?.tasks?.[t]?.done === true;

  const candidates: Array<[TaskKey, boolean]> = [
    ['profile', snapshot.companyProfileFilled],
    [
      'pipeline',
      snapshot.pipelineCount >= 1 && snapshot.stageCount >= 1 && snapshot.clientCount >= 1,
    ],
    ['goal', snapshot.salesTargetCount >= 1],
    ['lead', snapshot.clientCount >= 1],
    ['deals', snapshot.dealCount >= 1],
    ['team', snapshot.memberCount >= 2],
    ['automations', snapshot.workflowCount >= 1],
    ['integrations', snapshot.integrationCount >= 1],
    ['whatsapp', snapshot.wahaConfigCount >= 1],
  ];

  return candidates.filter(([task, met]) => met && !done(task)).map(([task]) => task);
}

